import React, { useState } from "react";
import axios from "axios";
import {
  Alert,
  AlertTriangle,
  Badge,
  Button,
  Check,
  Clock,
  LinkExternal01,
  RefreshCw01,
} from "@opendate-app/setlist";

import { toastSuccess, toastError } from "../../../../shared/toastHelper";
import { useMarketingSettingsContext } from "../MarketingSettingsIndexContainer";
import DnsRecordsTable from "./DnsRecordsTable";

const STATUS_BADGES = {
  enabled: { color: "success", icon: Check },
  active: { color: "blue", icon: Check },
  pending: { color: "warning", icon: Clock },
  pending_validation: { color: "warning", icon: Clock },
  blocked: { color: "error", icon: AlertTriangle },
  deleted: { color: "gray" },
};

const styles = {
  body: {
    display: "flex",
    flexDirection: "column",
    gap: 16,
    fontSize: 14,
    lineHeight: 1.5,
    color: "var(--sl-text-tertiary)",
  },
  statusRow: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 8,
  },
  label: {
    fontWeight: 600,
    color: "var(--sl-text-primary)",
  },
  hostname: {
    fontSize: 14,
    color: "var(--sl-text-primary)",
    overflowWrap: "anywhere",
  },
  strong: {
    color: "var(--sl-text-primary)",
  },
  heading: {
    fontWeight: 600,
    color: "var(--sl-text-primary)",
  },
  demoLink: {
    overflowWrap: "anywhere",
  },
  block: {
    display: "block",
  },
  section: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
    paddingTop: 20,
    borderTop: "1px solid var(--sl-border-secondary)",
  },
  title: {
    margin: 0,
    fontSize: 16,
    fontWeight: 600,
    color: "var(--sl-text-primary)",
  },
};

const TrackingStatusBadge = ({ status }) => {
  const badge = STATUS_BADGES[status] || STATUS_BADGES.pending;

  return (
    <Badge color={badge.color} icon={badge.icon}>
      {status?.toUpperCase().replace("_", " ") || "PENDING"}
    </Badge>
  );
};

const TrackingDomainSection = ({ sendDomain }) => {
  const {
    csrfToken,
    team,
    cloudflareCustomHostnames,
    setCloudflareCustomHostnames,
    opendateCustomerId,
  } = useMarketingSettingsContext();

  const [isLoading, setIsLoading] = useState(false);

  const expectedHostname = `ct.${sendDomain.domain}`;
  const customHostname = cloudflareCustomHostnames?.find(
    h => h.hostname === expectedHostname
  );

  const hasTrackingDomain = !!customHostname;
  const isVerified = customHostname?.["verified?"] || false;
  const isEnabled = customHostname?.["enabled?"] || false;
  const dnsRecords = customHostname?.dns_records || [];
  const demoLink = `https://${expectedHostname}/c/${opendateCustomerId}`;

  const setupClickTracking = () => {
    setIsLoading(true);
    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
    axios.post(
      `/teams/${team.id}/cloudflare_custom_hostnames`,
      {
        cloudflare_custom_hostname: {
          hostname: expectedHostname
        }
      }
    )
      .then(({ data }) => {
        toastSuccess("Click tracking provisioning started");
        setCloudflareCustomHostnames([...(cloudflareCustomHostnames || []), data.cloudflare_custom_hostname]);
      })
      .catch(({ response }) => {
        toastError(response?.data?.error || response?.data?.errors?.join(", ") || "Failed to setup click tracking");
      })
      .finally(() => setIsLoading(false));
  };

  const refreshStatus = () => {
    if (!customHostname) return;

    setIsLoading(true);
    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
    axios.post(
      `/teams/${team.id}/cloudflare_custom_hostnames/${customHostname.id}/verify`
    )
      .then(({ data }) => {
        setCloudflareCustomHostnames(
          cloudflareCustomHostnames.map(h => h.id === data.id ? data : h)
        );
        if (data["verified?"]) {
          toastSuccess("Click tracking is verified and ready to enable!");
        } else {
          toastSuccess("Status refreshed");
        }
      })
      .catch(({ response }) => {
        toastError(response?.data?.error || "Failed to refresh status");
      })
      .finally(() => setIsLoading(false));
  };

  const enableClickTracking = () => {
    if (!customHostname) return;

    setIsLoading(true);
    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
    axios.put(
      `/teams/${team.id}/cloudflare_custom_hostnames/${customHostname.id}`,
      {
        cloudflare_custom_hostname: {
          cloudflare_status: "enabled"
        }
      }
    )
      .then(({ data }) => {
        setCloudflareCustomHostnames(
          cloudflareCustomHostnames.map(h => h.id === data.id ? data : h)
        );
        toastSuccess("Click tracking enabled! All marketing emails will now use your branded domain.");
      })
      .catch(({ response }) => {
        toastError(response?.data?.error || response?.data?.errors?.join(", ") || "Failed to enable click tracking");
      })
      .finally(() => setIsLoading(false));
  };

  return (
    <section style={styles.section} aria-labelledby={`click-tracking-${sendDomain.id}`}>
      <h3 id={`click-tracking-${sendDomain.id}`} style={styles.title}>Branded click tracking</h3>
      <div style={styles.body}>
        <div>Use a custom domain for email tracking links to match your sending domain.</div>
        <div>Tracking links will use <b style={styles.strong}>{expectedHostname}</b> to match your sending domain.</div>

        {!hasTrackingDomain ? (
          <div>
            <Button variant="primary" onClick={setupClickTracking} disabled={isLoading}>
              {isLoading ? "Setting up..." : "Set up click tracking"}
            </Button>
          </div>
        ) : (
          <>
            <div style={styles.statusRow}>
              <span style={styles.label}>Tracking domain:</span>
              <code style={styles.hostname}>{customHostname.hostname}</code>
              <TrackingStatusBadge status={customHostname.cloudflare_status} />
            </div>

            {!isEnabled && (
              <div>
                <Button icon={RefreshCw01} onClick={refreshStatus} disabled={isLoading}>
                  {isLoading ? "Refreshing..." : "Refresh status"}
                </Button>
              </div>
            )}

            {isEnabled && (
              <Alert
                color="success"
                title="Click tracking is enabled"
                description={<>All tracking links will use <b>{customHostname.hostname}</b>.</>}
              />
            )}

            {isVerified && !isEnabled && (
              <Alert
                color="brand"
                title="DNS verified! Test before enabling"
                description={(
                  <>
                    <span style={styles.block}>Click the demo link below to verify the tracking domain is working correctly:</span>
                    <Button
                      as="a"
                      variant="link-color"
                      icon={LinkExternal01}
                      href={demoLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={styles.demoLink}
                    >
                      {demoLink}
                    </Button>
                  </>
                )}
                actions={(
                  <Button variant="primary" onClick={enableClickTracking} disabled={isLoading}>
                    {isLoading ? "Enabling..." : "Enable click tracking"}
                  </Button>
                )}
              />
            )}

            {!isVerified && dnsRecords.length > 0 && (
              <>
                <div>
                  <div style={styles.heading}>Configuration</div>
                  <div>To enable branded click tracking, you need to configure specific DNS records.</div>
                  <div>Log in to your domain registrar's website (e.g., GoDaddy, Namecheap, Google Domains).</div>
                  <div>Navigate to the DNS settings or DNS management section for your domain.</div>
                  <div>You need to add the following DNS records:</div>
                </div>
                <DnsRecordsTable
                  records={dnsRecords.map((record, index) => ({
                    key: index,
                    type: record.type,
                    host: record.name,
                    data: record.value,
                  }))}
                />
                <div>
                  <div style={styles.heading}>Verification</div>
                  <div>DNS changes can take some time to propagate. It may take anywhere from a few minutes to 48 hours.</div>
                  <div>Once the records have propagated, complete the verification process by clicking the "Refresh status" button above.</div>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </section>
  );
};

export default TrackingDomainSection;
